import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, ArrowLeft, Save, Archive, RotateCcw } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";

const SECTIONS = [
  ["executive_summary", "Executive Summary"], ["key_developments", "Key Developments"],
  ["verified_facts", "Verified Facts"], ["claims_under_investigation", "Claims Under Investigation"],
  ["contradictory_evidence", "Contradictory Evidence"], ["what_we_know", "What We Know"],
  ["what_we_do_not_know", "What We Do Not Know"], ["why_it_matters", "Why It Matters"],
  ["what_to_watch_next", "What to Watch Next"], ["sources", "Sources"],
];

export default function GhanaBriefs() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canManage = (user?.roles || []).some((r) => ["editor", "super_admin"].includes(r));
  const [briefs, setBriefs] = useState([]);
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(blank());
  const [tab, setTab] = useState("active"); // active | archived
  const [action, setAction] = useState(null); // { mode: 'archive'|'restore', brief }
  const [busy, setBusy] = useState(false);

  function blank() {
    const s = { title: "", record_ids: [] };
    SECTIONS.forEach(([k]) => (s[k] = ""));
    return s;
  }

  const loadBriefs = useCallback(() => {
    const params = tab === "archived" ? { archived: true } : {};
    api.get("/ghana-intel/briefs", { params }).then((r) => setBriefs(r.data)).catch(() => {});
  }, [tab]);
  const loadRecords = () => api.get("/ghana-intel/records").then((r) => setRecords(r.data)).catch(() => {});
  useEffect(() => { loadRecords(); }, []);
  useEffect(() => { loadBriefs(); }, [loadBriefs]);

  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/ghana-intel/briefs", form); toast.success("Brief created"); setOpen(false); setForm(blank()); loadBriefs(); openBrief(data.id); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openBrief = async (bid) => {
    try { const { data } = await api.get(`/ghana-intel/briefs/${bid}`); setActive(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveActive = async () => {
    try {
      const payload = { title: active.title, record_ids: active.record_ids };
      SECTIONS.forEach(([k]) => (payload[k] = active[k] || ""));
      await api.patch(`/ghana-intel/briefs/${active.id}`, payload);
      toast.success("Brief saved"); loadBriefs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleRec = (rid) => setForm((f) => ({ ...f, record_ids: f.record_ids.includes(rid) ? f.record_ids.filter((x) => x !== rid) : [...f.record_ids, rid] }));

  const runAction = async () => {
    if (busy || !action) return;
    setBusy(true);
    const { mode, brief } = action;
    try {
      if (mode === "archive") await api.delete(`/ghana-intel/briefs/${brief.id}`);
      else await api.post(`/ghana-intel/briefs/${brief.id}/restore`);
      toast.success(mode === "archive" ? "Brief archived" : "Brief restored");
      setAction(null);
      loadBriefs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (active) {
    return (
      <div className="p-6" data-testid="gi-brief-editor">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Briefs</button>
        <PageHeader title="Intelligence Brief" subtitle="Private until deliberately converted into Article Studio content.">
          <Btn data-testid="gi-brief-save" onClick={saveActive}><Save className="h-4 w-4" /> Save</Btn>
        </PageHeader>
        <Card className="p-4 space-y-4 max-w-3xl">
          <Field label="Title"><input className={inputCls} value={active.title} onChange={(e) => setActive({ ...active, title: e.target.value })} /></Field>
          {SECTIONS.map(([k, label]) => (
            <Field key={k} label={label}>
              <textarea data-testid={`gi-brief-${k}`} className={inputCls} rows={3} value={active[k] || ""} onChange={(e) => setActive({ ...active, [k]: e.target.value })} />
            </Field>
          ))}
          <div className="text-xs text-zinc-600">Linked records: {(active.records || []).map((r) => r.title).join(", ") || "none"}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <button onClick={() => nav("/command-center/ghana-intel")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Ghana Intelligence</button>
      <PageHeader title="Intelligence Briefs" subtitle="Manually assembled internal briefs (no AI generation).">
        <Btn data-testid="gi-new-brief-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Brief</Btn>
      </PageHeader>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-4">
        {["active", "archived"].map((t) => (
          <button key={t} data-testid={`gi-brief-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors capitalize ${tab === t ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t}
          </button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm" data-testid="gi-briefs-table">
          <thead><tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
            <th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Records</th>
            <th className="px-4 py-2.5">{tab === "archived" ? "Archived" : "Updated"}</th>
            {canManage && <th className="px-4 py-2.5">Actions</th>}
          </tr></thead>
          <tbody className="divide-y divide-zinc-900">
            {briefs.length === 0 && <tr><td colSpan={canManage ? 4 : 3} className="px-4 py-6 text-zinc-600" data-testid="gi-briefs-empty">{tab === "archived" ? "No archived briefs." : "No briefs yet."}</td></tr>}
            {briefs.map((b) => (
              <tr key={b.id} className={`hover:bg-zinc-900/50 ${tab === "active" ? "cursor-pointer" : ""}`} data-testid={`gi-brief-row-${b.id}`} onClick={() => tab === "active" && openBrief(b.id)}>
                <td className="px-4 py-2.5 font-medium">{b.title}</td>
                <td className="px-4 py-2.5 text-zinc-500">{b.record_ids?.length || 0}</td>
                <td className="px-4 py-2.5 text-zinc-600 text-xs">{(tab === "archived" ? b.deleted_at : b.updated_at) ? new Date(tab === "archived" ? b.deleted_at : b.updated_at).toLocaleDateString() : "—"}</td>
                {canManage && (
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {tab === "active" ? (
                      <button data-testid={`gi-brief-archive-${b.id}`} onClick={(e) => { e.stopPropagation(); setAction({ mode: "archive", brief: b }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors"><Archive className="h-3.5 w-3.5" /> Archive</button>
                    ) : (
                      <button data-testid={`gi-brief-restore-${b.id}`} onClick={(e) => { e.stopPropagation(); setAction({ mode: "restore", brief: b }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200 transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!action} onClose={() => { if (!busy) setAction(null); }} title={action?.mode === "archive" ? "Archive intelligence brief" : "Restore intelligence brief"}>
        {action && (
          <div className="space-y-4" data-testid="gi-brief-action-confirm">
            <p className="text-sm text-zinc-400">
              {action.mode === "archive"
                ? "Archive this brief? It is hidden from the active list but preserved with all content and linked records, and can be restored anytime."
                : "Restore this brief to the active list? All content and linked intelligence records are preserved."}
            </p>
            <div className="text-sm font-mono-data break-all" data-testid="gi-brief-action-title">{action.brief.title}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="gi-brief-action-cancel" disabled={busy} onClick={() => setAction(null)}>Cancel</Btn>
              <Btn data-testid="gi-brief-action-confirm-btn" disabled={busy} onClick={runAction} className="flex-1 justify-center">
                {busy ? "Working…" : action.mode === "archive" ? "Archive" : "Restore"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Intelligence Brief" wide>
        <form onSubmit={create} className="space-y-4">
          <Field label="Title"><input data-testid="gi-brief-title" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Include records">
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
              {records.map((r) => <button key={r.id} type="button" onClick={() => toggleRec(r.id)} className={`text-xs px-2 py-1 rounded-sm border ${form.record_ids.includes(r.id) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}>{r.title}</button>)}
              {records.length === 0 && <span className="text-xs text-zinc-600">No records yet.</span>}
            </div>
          </Field>
          <Btn data-testid="gi-brief-create" type="submit" className="w-full justify-center">Create Brief</Btn>
        </form>
      </Modal>
    </div>
  );
}
